import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, FormControl, FormHelperText, FormLabel, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { confiscateWallet } from "../groupsApi";
import { InfoBanner } from "../groupsUi";

/**
 * Validates the confiscation form.
 * @param {string} reason - Reason text
 * @returns {string|null}
 */
function validateForm(reason) {
    if (!reason?.trim()) return "Une raison est obligatoire pour confisquer le solde.";
    if (reason.trim().length < 10) return "La raison doit contenir au moins 10 caractères.";
    return null;
}

/**
 * Submits a confiscation request (4-eyes). The balance is not touched here:
 * a different super admin must approve the request afterwards.
 * @param {Object} args - Submission args
 */
async function submitConfiscate({ groupId, reason, setLoading, onClose, onConfiscated }) {
    const err = validateForm(reason);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        await confiscateWallet(groupId, { reason: reason.trim() });
        toast.success("Demande de confiscation créée. Un autre super admin doit l'approuver.");
        onConfiscated?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(typeof msg === "string" ? msg : "Échec de la demande de confiscation");
    } finally {
        setLoading(false);
    }
}

/**
 * Body of the confiscation dialog.
 * @param {Object} props - { wallet, reason, setReason }
 */
function DialogBody({ wallet, reason, setReason }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    const balance = Number(wallet?.balance ?? 0).toFixed(2);
    const currency = (wallet?.currency || "").toUpperCase();
    return (
        <Stack spacing="16px">
            <InfoBanner tone="danger">
                Crée une demande de confiscation (4 yeux). Le solde n'est pas touché : un autre super admin devra approuver. La confiscation est irréversible une fois approuvée.
            </InfoBanner>
            <Text color={textColor} fontSize="0.85rem">Solde actuel : <strong>{balance} {currency}</strong></Text>
            <FormControl isRequired>
                <FormLabel fontSize="0.8rem">Raison</FormLabel>
                <Textarea size="sm" borderRadius="8px" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                <FormHelperText fontSize="0.7rem">Min. 10 caractères. Sera persistée dans la transaction de confiscation.</FormHelperText>
            </FormControl>
        </Stack>
    );
}

/**
 * Confirmation dialog for confiscating a wallet's balance (super-admin).
 * @param {Object} props - { open, groupId, wallet, onClose, onConfiscated }
 */
function ConfiscateWalletDialog({ open, groupId, wallet, onClose, onConfiscated }) {
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setReason(""); }, [open]);

    const onSubmit = () => submitConfiscate({ groupId, reason, setLoading, onClose, onConfiscated });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Demander la confiscation</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody wallet={wallet} reason={reason} setReason={setReason} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="red" onClick={onSubmit} isLoading={loading} loadingText="Envoi...">Créer la demande</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ConfiscateWalletDialog;
